import React from 'react';
import { formatSouthAfricanDateTime } from '../../utils/southAfricanTime';
import {
  RISK_AREAS, SECTIONS, TEMPLATE_VERSION, Values, areaFindings, areaRating, cashSurplus, exposureAfter, flags, netTransaction,
  overallRating, recognisedValue, securityCover, totalToRepay,
} from '../pages/loan-processing/creditAssessmentTemplate';
import {
  DECISION_LABELS, OUTCOME_LABELS, SECTIONS as REV0_SECTIONS,
} from '../pages/loan-processing/creditReportModel';

/**
 * A saved Credit Assessment Report, read only, for Credit Committee and for anyone looking at the
 * application or the loan afterwards. Only what was captured is shown: empty fields, untouched controls
 * and calculations without their figures are left out (Pule, 29 Oct 2026).
 */

const money = (n: number) => n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });
const has = (x: unknown) => x !== undefined && x !== null && String(x).trim() !== '';
const OUTCOME_CLASS: Record<string, string> = {
  'Pass': 'bg-success', 'Pass with Mitigation': 'bg-info text-dark', 'Fail': 'bg-danger', 'Hard Stop': 'bg-dark', 'N/A': 'bg-secondary',
};
const RATING_CLASS: Record<string, string> = { High: 'bg-danger', Moderate: 'bg-warning text-dark', Low: 'bg-success', 'N/A': 'bg-secondary', Incomplete: 'bg-secondary' };

const Line: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="col-md-6">
    <div className="small text-muted">{label}</div>
    <div style={{ whiteSpace: 'pre-wrap' }}>{children}</div>
  </div>
);

const shown = (v: Values, key: string, kind: string) => (kind === 'money' ? money(Number(v[key])) : String(v[key]));

/** The 27 Oct 2026 template */
const TemplateView: React.FC<{ v: Values }> = ({ v }) => {
  const f = flags(v);
  const overall = overallRating(v);
  const calc = (label: string, value?: number) => (value === undefined ? null : (
    <div className="d-flex justify-content-between"><span>{label}</span><strong className={value < 0 ? 'text-danger' : ''}>{money(value)}</strong></div>
  ));
  return (
    <>
      {(f.hardStops.length > 0 || f.failed.length > 0) && (
        <div className="d-grid gap-2 mb-3 small">
          {f.hardStops.length > 0 && <div className="alert alert-dark mb-0"><strong>Hard Stop:</strong> {f.hardStops.join(' · ')}</div>}
          {f.failed.length > 0 && <div className="alert alert-danger mb-0"><strong>Failed:</strong> {f.failed.join(' · ')}</div>}
        </div>
      )}

      {SECTIONS.map(s => {
        const fields = (s.fields || []).filter(x => has(v[`${s.key}.${x.key}`]));
        const rows = (s.rows || []).filter(r => {
          const status = v[`${s.key}.${r.key}.status`], outcome = v[`${s.key}.${r.key}.outcome`];
          const untouched = (status === 'Not required' || !has(status)) && (outcome === 'N/A' || !has(outcome))
            && !has(v[`${s.key}.${r.key}.position`]) && !has(v[`${s.key}.${r.key}.condition`]);
          return !untouched;
        });
        const outcome = s.outcome ? v[`${s.key}.outcome`] as string | undefined : undefined;
        const calcs = s.key === 's1' ? [calc('Estimated Normal Monthly Cash Surplus / (Deficit)', cashSurplus(v))]
          : s.key === 's2' ? [calc('Total Amount to Repay Tirhelo', totalToRepay(v)), calc('Expected Net Transaction Surplus / (Deficit)', netTransaction(v))]
            : s.key === 's9' && has(v['s9.retailValue']) ? [
              calc(`Recognised collateral value (${v['s9.recognisedPercent'] ?? 70}% of retail)`, recognisedValue(v)),
              calc('Tirhelo exposure after the proposed disbursement', exposureAfter(v)),
              calc('Security surplus / (shortfall)', securityCover(v))] : [];
        const anyCalc = calcs.some(Boolean);
        if (!fields.length && !rows.length && !outcome && !anyCalc) return null;
        return (
          <div key={s.key} className="border rounded p-3 mb-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">{s.title}</h6>
              {outcome && <span className={`badge ${OUTCOME_CLASS[outcome] || 'bg-secondary'}`}>{outcome}</span>}
            </div>
            {fields.length > 0 && (
              <div className="row g-2">{fields.map(x => <Line key={x.key} label={x.label}>{shown(v, `${s.key}.${x.key}`, x.kind)}</Line>)}</div>
            )}
            {rows.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm mb-0 small">
                  <thead className="text-muted"><tr><th>Control</th><th>Status</th><th>Key position</th><th>Outcome</th><th>Condition / control</th></tr></thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.key}>
                        <td className="fw-semibold">{r.label}</td>
                        <td>{String(v[`s9.${r.key}.status`] || '')}</td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{String(v[`s9.${r.key}.position`] || '')}</td>
                        <td>{has(v[`s9.${r.key}.outcome`]) && <span className={`badge ${OUTCOME_CLASS[String(v[`s9.${r.key}.outcome`])] || 'bg-secondary'}`}>{String(v[`s9.${r.key}.outcome`])}</span>}</td>
                        <td>{String(v[`s9.${r.key}.condition`] || '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {anyCalc && <div className="bg-light rounded p-2 mt-2 small">{calcs}</div>}
          </div>
        );
      })}

      {RISK_AREAS.some(a => areaRating(v, a) !== 'Incomplete') && (
        <div className="border rounded p-3 mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">11. Overall Risk Assessment</h6>
            {overall !== 'Incomplete' && <span className={`badge ${RATING_CLASS[overall]}`}>Overall: {overall}</span>}
          </div>
          <table className="table table-sm mb-0 small">
            <tbody>
              {RISK_AREAS.filter(a => areaRating(v, a) !== 'Incomplete').map(a => (
                <tr key={a.key}>
                  <td className="fw-semibold" style={{ width: '30%' }}>{a.label}</td>
                  <td style={{ width: 110 }}><span className={`badge ${RATING_CLASS[areaRating(v, a)]}`}>{areaRating(v, a)}</span></td>
                  <td>{areaFindings(v, a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(has(v['s12.decision']) || has(v['s12.rationale'])) && (
        <div className="border rounded p-3 mb-2">
          <h6 className="mb-2">12. Credit Recommendation</h6>
          <div className="row g-2">
            {has(v['s12.decision']) && <Line label="Final Recommendation">{String(v['s12.decision'])}</Line>}
            {has(v['s12.rationale']) && <div className="col-12"><div className="small text-muted">Decision Rationale</div><div style={{ whiteSpace: 'pre-wrap' }}>{String(v['s12.rationale'])}</div></div>}
          </div>
        </div>
      )}
    </>
  );
};

/** A report saved on the earlier template (rev0): its captured fields, row values and outcomes */
const Rev0View: React.FC<{ v: Values }> = ({ v }) => (
  <>
    {REV0_SECTIONS.map(s => {
      const fields = (s.fields || []).filter(x => has(v[`${s.key}.${x.key}`]));
      const rows = (s.rows || []).map(r => ({ r, cells: (s.rowFields || []).filter(c => has(v[`${s.key}.${r.key}.${c.key}`])) }))
        .filter(x => x.cells.length > 0);
      const outcome = v[`${s.key}.outcome`] as keyof typeof OUTCOME_LABELS | undefined;
      const decision = s.key === 's11' ? v['s11.decision'] as keyof typeof DECISION_LABELS | undefined : undefined;
      if (!fields.length && !rows.length && !outcome && !decision) return null;
      return (
        <div key={s.key} className="border rounded p-3 mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">{s.title}</h6>
            {outcome && <span className="badge bg-secondary">{OUTCOME_LABELS[outcome]}</span>}
          </div>
          {decision && <p className="mb-2"><strong>Final recommendation:</strong> {DECISION_LABELS[decision]}</p>}
          {fields.length > 0 && <div className="row g-2">{fields.map(x => <Line key={x.key} label={x.label}>{shown(v, `${s.key}.${x.key}`, x.kind)}</Line>)}</div>}
          {rows.map(({ r, cells }) => (
            <div key={r.key} className="small mt-1"><strong>{r.label}:</strong> {cells.map(c => `${c.label}: ${shown(v, `${s.key}.${r.key}.${c.key}`, c.kind)}`).join(' · ')}</div>
          ))}
        </div>
      );
    })}
  </>
);

const CreditAssessmentReportView: React.FC<{ report: { content?: object; updatedBy?: string; updatedAt?: string } }> = ({ report }) => {
  const v = (report.content || {}) as Values;
  return (
    <>
      <p className="small text-muted mb-3">
        {v._template === TEMPLATE_VERSION ? 'Credit Assessment Template 27.10.2026' : 'Earlier Credit Assessment Report template'}
        {report.updatedBy ? ` · saved by ${report.updatedBy}` : ''}{report.updatedAt ? ` · ${formatSouthAfricanDateTime(report.updatedAt)}` : ''}
      </p>
      {v._template === TEMPLATE_VERSION ? <TemplateView v={v} /> : <Rev0View v={v} />}
    </>
  );
};

export default CreditAssessmentReportView;
